<template>
  <Teleport to="body">
    <Transition name="popup-backdrop">
      <div
        v-if="visible"
        class="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm"
        @click="onDismiss"
      />
    </Transition>

    <Transition name="popup-panel">
      <div
        v-if="visible"
        class="fixed inset-0 z-[10001] flex items-center justify-center p-4"
        role="alertdialog"
        aria-modal="true"
      >
        <div class="relative w-full max-w-sm">
          <div
            class="relative overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-2xl dark:border-emerald-800/40 dark:bg-gray-900"
          >
            <div
              class="h-1.5 w-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-green-600"
            />

            <button
              ref="closeButtonRef"
              type="button"
              class="absolute right-4 top-4 z-10 rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
              @click="onDismiss"
            >
              <X class="h-4 w-4" />
            </button>

            <div class="flex flex-col items-center px-6 pt-6 pb-4 text-center">
              <div
                class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 ring-4 ring-emerald-100 dark:bg-emerald-900/20 dark:ring-emerald-900/10"
              >
                <CheckCircle2
                  class="h-8 w-8 text-emerald-500 dark:text-emerald-400"
                  stroke-width="2"
                />
              </div>

              <p class="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {{ message }}
              </p>
            </div>

            <div class="mx-8 border-t border-gray-100 dark:border-gray-700/60" />

            <div class="flex justify-center px-8 py-4">
              <button
                type="button"
                class="rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 px-8 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition-all duration-200 hover:from-emerald-600 hover:to-green-700 hover:shadow-emerald-500/50 active:scale-95"
                @click="onDismiss"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { CheckCircle2, X } from 'lucide-vue-next'
import router from '@/router'

const props = defineProps<{
  visible: boolean
  message: string
  title?: string
  duration?: number
  route?: string
}>()

const emit = defineEmits<{ close: [] }>()

const closeButtonRef = ref<HTMLButtonElement | null>(null)

function onDismiss(): void {
  emit('close')

  if (props.route) {
    void router.push(props.route)
  }
}
watch(
  () => props.visible,
  async (isVisible) => {
    if (!isVisible) return

    await nextTick()
    closeButtonRef.value?.focus()
  }
)
</script>

<style scoped>
.popup-backdrop-enter-active,
.popup-backdrop-leave-active {
  transition: opacity 0.2s ease;
}

.popup-backdrop-enter-from,
.popup-backdrop-leave-to {
  opacity: 0;
}

.popup-panel-enter-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.popup-panel-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.popup-panel-enter-from,
.popup-panel-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}
</style>
